<template>
  <v-dialog v-model="showDialog" persistent :max-width="dialogMaxWidth" class="pcd-wrapper">
    <div class="pcd-card">
      <!-- Animated Background Blobs -->
      <div class="pcd-blob pcd-blob-1"></div>
      <div class="pcd-blob pcd-blob-2"></div>

      <!-- Icon Area -->
      <div class="pcd-icon-wrap">
        <transition name="pcd-icon-fade" mode="out-in">
          <div v-if="phase === 'loading'" key="loading" class="pcd-spinner-ring">
            <svg class="pcd-ring-svg" viewBox="0 0 56 56">
              <defs>
                <linearGradient id="pcdGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#3b82f6" />
                  <stop offset="100%" stop-color="#8b5cf6" />
                </linearGradient>
              </defs>
              <circle class="pcd-ring-track" cx="28" cy="28" r="22" />
              <circle class="pcd-ring-fill" cx="28" cy="28" r="22" :style="ringStyle" />
            </svg>
            <div class="pcd-logo-inner">
              <svg viewBox="0 0 24 24" class="pcd-person-icon">
                <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
              </svg>
            </div>
          </div>

          <div v-else-if="phase === 'success'" key="success" class="pcd-result-icon pcd-result-success">
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
          </div>

          <div v-else-if="phase === 'partial'" key="partial" class="pcd-result-icon pcd-result-partial">
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
            </svg>
          </div>

          <div v-else-if="phase === 'error'" key="error" class="pcd-result-icon pcd-result-error">
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </div>
        </transition>
      </div>

      <!-- Title -->
      <transition name="pcd-fade" mode="out-in">
        <h2 class="pcd-title" :key="currentTitle">{{ currentTitle }}</h2>
      </transition>

      <!-- Step List -->
      <transition name="pcd-fade" mode="out-in">
        <div v-if="phase === 'loading'" key="steps" class="pcd-steps">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="pcd-step"
            :class="{
              'pcd-step-done': step.done,
              'pcd-step-active': step.active,
              'pcd-step-pending': !step.done && !step.active
            }"
          >
            <span class="pcd-step-dot">
              <svg v-if="step.done" viewBox="0 0 24 24" class="pcd-step-check">
                <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
              </svg>
              <span v-else-if="step.active" class="pcd-step-pulse"></span>
              <span v-else class="pcd-step-empty"></span>
            </span>
            <span class="pcd-step-label">{{ step.label }}</span>
          </div>
        </div>

        <!-- Result Message -->
        <div v-else key="result" class="pcd-result-body">
          <p class="pcd-result-msg">{{ currentMessage }}</p>
          <button
            v-if="phase === 'error'"
            class="pcd-contact-btn"
            @click="closeDialog"
          >
            {{ t('profile-dialog.close') }}
          </button>
          <p v-if="phase === 'partial'" class="pcd-result-sub">{{ t('profile-dialog.partial-sub') }}</p>
        </div>
      </transition>

      <!-- Progress Bar -->
      <div v-if="phase === 'loading'" class="pcd-progress-bar">
        <div class="pcd-progress-fill" :style="{ width: progressPct + '%' }"></div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, defineExpose } from 'vue'
import { useOAuthService } from '@/services/OAuthService'
import lodash from 'lodash'
import { getStoredItem, setStoredItem, removeStoredItem } from '@/services/utils'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { useI18n } from 'vue-i18n'
import {
  getBuildings,
  getCampuses,
  getUserProfiles,
  getDataFromBlob,
  getFile,
  deleteProfileImage,
  postProfileImage,
  postFile,
  createUserProfile,
  getUserProfileById,
  updateUserProfile,
} from '@/controllers/BaseController'
import { getUsers, updateUser } from '@/controllers/BackboneAPI'
import { Capacitor } from '@capacitor/core'

const OAuthService = useOAuthService()
const { t } = useI18n()

const showDialog = ref(false)
// phase: 'loading' | 'success' | 'partial' | 'error'
const phase = ref('loading')
const progressPct = ref(0)
const ringOffset = ref(138) // circumference of r=22 circle

const ringStyle = computed(() => ({
  strokeDashoffset: ringOffset.value + 'px'
}))

const dialogMaxWidth = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 480 ? '92vw' : '420'
  }
  return '420'
})

// Steps shown during loading
const steps = ref([
  { label: '', done: false, active: false },
  { label: '', done: false, active: false },
  { label: '', done: false, active: false },
  { label: '', done: false, active: false },
])

const initStepLabels = () => {
  steps.value[0].label = t('profile-dialog.step-load')
  steps.value[1].label = t('profile-dialog.step-profile')
  steps.value[2].label = t('profile-dialog.step-roles')
  steps.value[3].label = t('profile-dialog.step-finalize')
}

const currentTitle = computed(() => {
  if (phase.value === 'loading') return t('profile-dialog.creating-title')
  if (phase.value === 'success') return t('profile-dialog.ready-title')
  if (phase.value === 'partial') return t('profile-dialog.partial-title')
  return t('profile-dialog.error-title')
})

const currentMessage = computed(() => {
  if (phase.value === 'success') return t('profile-dialog.success-message-auto')
  if (phase.value === 'partial') return t('profile-dialog.partial-message')
  return t('profile-dialog.error-message')
})

// Animate progress ring (0–100 maps to circumference 138→0)
const CIRCUM = 138.23
const setProgress = (pct) => {
  progressPct.value = pct
  ringOffset.value = CIRCUM - (CIRCUM * pct) / 100
}

const activateStep = (i) => {
  steps.value.forEach((s, idx) => {
    if (idx < i) { s.done = true; s.active = false }
    else if (idx === i) { s.done = false; s.active = true }
    else { s.done = false; s.active = false }
  })
}

const completeStep = (i) => {
  steps.value[i].done = true
  steps.value[i].active = false
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

const runProfileCreation = async () => {
  let partial = false

  try {
    // Step 0 – Load user data
    activateStep(0)
    setProgress(5)

    let userProfile
    let apiProfilesFetched, usersFetched

    try {
      userProfile = await OAuthService.getUserProfile()
      ;[apiProfilesFetched, usersFetched] = await Promise.all([getUserProfiles(), getUsers()])
    } catch {
      phase.value = 'error'
      return
    }

    completeStep(0)
    setProgress(25)
    await sleep(200)

    // Step 1 – Create / update profile
    activateStep(1)
    setProgress(35)

    let apiProfile = apiProfilesFetched.find((u) => u.email === userProfile.email)
    if (apiProfile) {
      try {
        apiProfile = await getUserProfileById(apiProfile.id)
      } catch { /* use existing */ }
    }

    let [firstName, lastName] = lodash.split(
      userProfile.name || lodash.replace(userProfile.email.split('@')[0], /\./g, ' '),
      ' '
    )
    firstName = lodash.capitalize(firstName || '')
    lastName = lodash.capitalize(lastName || 'Unknown')
    const fullName = `${firstName} ${lastName}`

    const selectedBuilding =
      lodash.find(await getBuildings().catch(() => []), { name: await getStoredItem('selectedBuilding') }) || {}
    const selectedCampus =
      lodash.find(await getCampuses().catch(() => []), { id: lodash.get(selectedBuilding, 'campus.id') }) || {}

    const profilePayload = {
      name: fullName,
      displayName: fullName,
      email: userProfile.email,
      displayEmail: userProfile.email,
      active: true,
      campusId: selectedCampus.id,
    }

    let profileId
    try {
      if (apiProfile) {
        const merged = { ...apiProfile, ...profilePayload, version: apiProfile.version }
        await updateUserProfile(apiProfile.id, merged)
        profileId = apiProfile.id
      } else {
        const newProfile = await createUserProfile(profilePayload)
        profileId = newProfile.id
      }
    } catch {
      partial = true
    }

    completeStep(1)
    setProgress(60)
    await sleep(200)

    // Step 2 – Sync user account + roles
    activateStep(2)
    setProgress(70)

    const apiUser = usersFetched.find((u) => u.email === userProfile.email)
    if (apiUser) {
      try {
        const userPayload = {
          ...apiUser,
          name: fullName,
          email: userProfile.email,
          active: true,
          campusId: selectedCampus.id,
          firstName,
          lastName,
        }
        await updateUser(apiUser.id, userPayload)
      } catch {
        partial = true
      }
    }

    completeStep(2)
    setProgress(85)
    await sleep(200)

    // Step 3 – Finalize & cache
    activateStep(3)
    setProgress(92)

    try {
      await Promise.all([
        removeStoredItem('userProfiles'),
        removeStoredItem('users'),
      ])
      if (apiProfile?.id) await removeStoredItem(`userProfile_${apiProfile.id}`)
    } catch { /* non-critical */ }

    setStoredItem('ProfileCompleted', true)

    completeStep(3)
    setProgress(100)
    await sleep(400)

    phase.value = partial ? 'partial' : 'success'

    // Auto-close and reload after showing result
    await sleep(2200)
    window.location.reload()

  } catch {
    phase.value = 'error'
  }
}

const closeDialog = () => {
  showDialog.value = false
}

const openDialog = () => {
  phase.value = 'loading'
  progressPct.value = 0
  ringOffset.value = CIRCUM
  steps.value.forEach((s) => { s.done = false; s.active = false })
  initStepLabels()
  showDialog.value = true
  // kick off immediately
  runProfileCreation()
}

defineExpose({ openDialog })
</script>

<style scoped>
/* ── Wrapper ── */
.pcd-wrapper {
  z-index: 9999;
}

/* ── Card ── */
.pcd-card {
  position: relative;
  background: #ffffff;
  border-radius: 28px;
  overflow: hidden;
  padding: 40px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
  animation: pcdSlide 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes pcdSlide {
  from { opacity: 0; transform: translateY(32px) scale(0.94); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}

/* ── Decorative blobs ── */
.pcd-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;
  z-index: 0;
}
.pcd-blob-1 {
  width: 260px; height: 260px;
  background: radial-gradient(circle, rgba(99,179,237,0.18) 0%, transparent 70%);
  top: -80px; right: -60px;
}
.pcd-blob-2 {
  width: 200px; height: 200px;
  background: radial-gradient(circle, rgba(154,103,246,0.12) 0%, transparent 70%);
  bottom: -60px; left: -40px;
}

/* ── Spinner Ring ── */
.pcd-icon-wrap {
  position: relative;
  z-index: 1;
}

.pcd-spinner-ring {
  width: 88px;
  height: 88px;
  position: relative;
}

.pcd-ring-svg {
  width: 88px;
  height: 88px;
  transform: rotate(-90deg);
  animation: pcdRotate 1.6s linear infinite;
}

@keyframes pcdRotate {
  to { transform: rotate(270deg); }
}

.pcd-ring-track {
  fill: none;
  stroke: #e8f4fd;
  stroke-width: 4;
}

.pcd-ring-fill {
  fill: none;
  stroke: url(#pcdGrad);
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: 138.23;
  transition: stroke-dashoffset 0.5s ease;
}

.pcd-logo-inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pcd-person-icon {
  width: 36px;
  height: 36px;
  color: #3b82f6;
  animation: pcdPulse 1.6s ease-in-out infinite;
}

@keyframes pcdPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.92); }
}

/* ── Result Icons ── */
.pcd-result-icon {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pcdPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.pcd-result-icon svg {
  width: 44px;
  height: 44px;
}

@keyframes pcdPop {
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1);   }
}

.pcd-result-success {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  box-shadow: 0 8px 24px rgba(5, 150, 105, 0.25);
}

.pcd-result-partial {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #d97706;
  box-shadow: 0 8px 24px rgba(217, 119, 6, 0.25);
}

.pcd-result-error {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #dc2626;
  box-shadow: 0 8px 24px rgba(220, 38, 38, 0.25);
}

/* ── Title ── */
.pcd-title {
  position: relative;
  z-index: 1;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  text-align: center;
  letter-spacing: -0.3px;
}

/* ── Steps ── */
.pcd-steps {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pcd-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.pcd-step-done {
  background: #f0fdf4;
}

.pcd-step-active {
  background: #eff6ff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.pcd-step-pending {
  background: #f8fafc;
}

.pcd-step-dot {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pcd-step-check {
  width: 20px;
  height: 20px;
  color: #10b981;
}

.pcd-step-pulse {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #3b82f6;
  animation: pcdDotPulse 1s ease-in-out infinite;
}

@keyframes pcdDotPulse {
  0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(59,130,246,0.4); }
  50%       { transform: scale(1.2); opacity: 0.8; box-shadow: 0 0 0 6px rgba(59,130,246,0); }
}

.pcd-step-empty {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
}

.pcd-step-label {
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  transition: color 0.3s ease;
}

.pcd-step-done .pcd-step-label { color: #10b981; }
.pcd-step-active .pcd-step-label { color: #3b82f6; font-weight: 600; }

/* ── Result Body ── */
.pcd-result-body {
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.pcd-result-msg {
  font-size: 15px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  max-width: 300px;
}

.pcd-result-sub {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

.pcd-contact-btn {
  margin-top: 8px;
  padding: 10px 28px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);
  transition: all 0.3s ease;
}

.pcd-contact-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.45);
}

/* ── Progress Bar ── */
.pcd-progress-bar {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 5px;
  background: #e2e8f0;
  border-radius: 99px;
  overflow: hidden;
}

.pcd-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  border-radius: 99px;
  transition: width 0.5s ease;
}

/* ── Transitions ── */
.pcd-fade-enter-active,
.pcd-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.pcd-fade-enter-from   { opacity: 0; transform: translateY(8px); }
.pcd-fade-leave-to     { opacity: 0; transform: translateY(-8px); }

.pcd-icon-fade-enter-active,
.pcd-icon-fade-leave-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.pcd-icon-fade-enter-from   { opacity: 0; transform: scale(0.6); }
.pcd-icon-fade-leave-to     { opacity: 0; transform: scale(1.2); }

/* ── Mobile ── */
@media (max-width: 480px) {
  .pcd-card {
    padding: 32px 20px 24px;
    border-radius: 22px;
    gap: 16px;
  }

  .pcd-title {
    font-size: 18px;
  }

  .pcd-step-label {
    font-size: 13px;
  }

  .pcd-result-msg {
    font-size: 14px;
  }

  .pcd-spinner-ring,
  .pcd-result-icon {
    width: 76px;
    height: 76px;
  }

  .pcd-ring-svg {
    width: 76px;
    height: 76px;
  }

  .pcd-result-icon svg {
    width: 38px;
    height: 38px;
  }
}

@media (max-width: 360px) {
  .pcd-card {
    padding: 28px 16px 20px;
  }

  .pcd-title {
    font-size: 16px;
  }
}
</style>
